import React from "react";
import { useSelector } from "react-redux";
import { PortalItineraryStatus } from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import dayjs from "dayjs";

import { StatusCopyKeyMap } from "../../../../utils";
import { getAirlines, getAirports } from "../../store/selectors";
import type { FlightItineraryCardProps } from "./types";

const DATE_FORMAT = DateTimeFormatStyle.ShortMonthDayShortWeekday;
const MEDIUM_DATE_FORMAT = DateTimeFormatStyle.MediumDate;
const SHORT_TIME_FORMAT = DateTimeFormatStyle.ShortTime;

export const useFlightItineraryCard = ({
  itinerary,
  selectItinerary,
}: FlightItineraryCardProps) => {
  const { t, formatDateTime } = useI18nContext();
  const airports = useSelector(getAirports);
  const airlines = useSelector(getAirlines);
  const status = itinerary.status;
  const {
    scheduleChange,
    travelItinerary: { slices, locators },
  } = itinerary.bookedItinerary;
  const b2bLocator = locators ? locators.b2b : "fake";
  const outboundSlice = slices[0];
  const outboundSegments = outboundSlice.segments;
  const firstSegment = outboundSegments[0];
  const lastSegment = outboundSegments[outboundSegments.length - 1];
  const origin = firstSegment.origin;
  const destination = lastSegment.destination;
  const airline = firstSegment.marketingAirline;
  const hasScheduleChange = Boolean(scheduleChange);

  const returnFirstSegment = slices.length === 2 ? slices[1].segments[0] : null;
  const returnAirline = returnFirstSegment?.marketingAirline ?? airline;
  const returnDate =
    returnFirstSegment != null
      ? formatDateTime(
          dayjs(
            returnFirstSegment.updatedDeparture ??
              returnFirstSegment.scheduledDeparture
          ).toDate(),
          DATE_FORMAT
        )
      : null;
  const showStatusTag =
    (status === PortalItineraryStatus.Modified && !hasScheduleChange) ||
    status !== PortalItineraryStatus.Confirmed;

  const originCity =
    airports?.[origin.locationCode]?.cityName ?? origin.locationCode;
  const destinationCity =
    airports?.[destination.locationCode]?.cityName ?? destination.locationCode;

  return React.useMemo(
    () => ({
      context: {
        hasScheduleChange,
        showStatusTag,
      },
      text: {
        destinationCity: destinationCity,
        originCity: originCity,
        title: t("core-flights.itinerary.card.itinerary", {
          origin: `${originCity} (${origin.locationCode})`,
          destination: `${destinationCity} (${destination.locationCode})`,
          interpolation: { escapeValue: false },
        }),
        tripType: returnFirstSegment != null ? t("roundTrip") : t("oneWay"),
        tripCategory:
          slices.length === 2
            ? t("core-flights.itinerary.card.roundTrip")
            : t("core-flights.itinerary.card.oneWay"),
        departureDate: formatDateTime(
          dayjs(
            firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture
          ).toDate(),
          MEDIUM_DATE_FORMAT
        ),
        returnDate: returnFirstSegment
          ? formatDateTime(
              dayjs(
                returnFirstSegment.updatedDeparture ??
                  returnFirstSegment.scheduledDeparture
              ).toDate(),
              MEDIUM_DATE_FORMAT
            )
          : "",
        departureTime: formatDateTime(
          dayjs(
            firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture
          ).toDate(),
          SHORT_TIME_FORMAT
        ),
        returnTime: returnFirstSegment
          ? formatDateTime(
              dayjs(
                returnFirstSegment.updatedDeparture ??
                  returnFirstSegment.scheduledDeparture
              ).toDate(),
              SHORT_TIME_FORMAT
            )
          : "",
        dates: [
          formatDateTime(
            dayjs(
              firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture
            ).toDate(),
            DATE_FORMAT
          ),
          returnDate,
        ]
          .filter((date) => date != null)
          .join(" - "),
        action: t("core-flights.itinerary.card.viewDetails"),
        airline: airlines?.[airline.code]?.displayName ?? airline.code,
        airlineCode: airline.code,
        confirmation: t("core-flights.itinerary.card.confirmation", {
          number: b2bLocator,
        }),
        flightNumber: `${airline.code} ${airline.flightNumber}`,
        returnAirline:
          airlines?.[returnAirline.code]?.displayName ?? returnAirline.code,
        returnAirlineCode: returnAirline.code,
        returnFlightNumber: `${returnAirline.code} ${returnAirline.flightNumber}`,
        statusText: t(StatusCopyKeyMap[status]),
      },
      handlers: {
        selectItinerary,
      },
    }),
    [
      t,
      origin.locationCode,
      destination.locationCode,
      returnFirstSegment,
      slices.length,
      formatDateTime,
      firstSegment.updatedDeparture,
      firstSegment.scheduledDeparture,
      returnAirline,
      returnDate,
      airlines,
      airline.code,
      airline.flightNumber,
      b2bLocator,
      hasScheduleChange,
      selectItinerary,
      showStatusTag,
      status,
      destinationCity,
      originCity,
    ]
  );
};
